import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useAuth, formatError } from "@/context/AuthContext";
import { LogoFull } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordInput } from "@/components/PasswordInput";

const dest = (u) => u.role === "loueur" ? "/loueur" : u.role === "partner" ? "/partenaire" : (u.role === "superadmin" || u.role === "sous-admin") ? "/admin" : "/my/bookings";

export default function Login() {
  const { t } = useTranslation();
  const { login, verify2fa } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(form.email, form.password);
      if (res.two_factor) { setChallenge(res.challenge_id); toast.info(t("auth.codeSentToast")); }
      else { toast.success(t("auth.welcome")); nav(dest(res.user)); }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setLoading(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verify2fa(challenge, code, remember);
      toast.success(t("auth.welcome"));
      nav(dest(u));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="section-sand min-h-[80vh] flex items-center justify-center py-16 px-4">
      <div className="bg-white rounded-3xl shadow-card border border-border p-8 sm:p-10 max-w-md w-full">
        <div className="flex justify-center mb-6"><LogoFull /></div>
        {!challenge ? (
          <>
            <h1 className="font-heading font-extrabold text-2xl text-ink text-center">{t("auth.login")}</h1>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Input data-testid="login-email" required type="email" placeholder={t("auth.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 rounded-xl bg-sand" />
              <PasswordInput data-testid="login-password" required placeholder={t("auth.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12 rounded-xl bg-sand" />
              <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-12 rounded-full bg-canard hover:bg-canard-dark font-bold">{loading ? "..." : t("auth.signin")}</Button>
            </form>
            <p className="text-center text-sm text-muted2 font-body mt-6">{t("auth.noAccount")} <Link to="/register" data-testid="go-register-link" className="text-emeraude font-bold hover:underline">{t("auth.register")}</Link></p>
            <p className="text-center text-sm mt-3"><Link to="/forgot-password" data-testid="forgot-password-link" className="text-canard font-semibold hover:underline">{t("auth.forgotPwd")}</Link></p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-canard/10 flex items-center justify-center mx-auto mb-4"><ShieldCheck className="w-8 h-8 text-canard" /></div>
            <h1 className="font-heading font-extrabold text-2xl text-ink text-center">{t("profile.twoFATitle")}</h1>
            <p className="text-center text-sm text-muted2 font-body mt-2">{t("profile.twoFASub")}</p>
            <form onSubmit={verify} className="mt-6 space-y-4">
              <Input data-testid="twofa-code" required inputMode="numeric" maxLength={6} placeholder="______" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-14 rounded-xl bg-sand text-center text-2xl tracking-[0.5em] font-mono" />
              <label className="flex items-center gap-2 cursor-pointer text-sm text-muted2 font-body">
                <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="twofa-remember" /> {t("profile.remember")}
              </label>
              <Button type="submit" data-testid="twofa-submit" disabled={loading || code.length !== 6} className="w-full h-12 rounded-full bg-emeraude hover:bg-emeraude-dark font-bold">{loading ? "..." : t("profile.validate")}</Button>
            </form>
            <button onClick={() => { setChallenge(null); setCode(""); }} data-testid="twofa-back" className="w-full text-center text-sm text-muted2 hover:text-canard font-semibold mt-4">{t("profile.back")}</button>
          </>
        )}
      </div>
    </div>
  );
}
